/**
 * StripeTrackingSetupPanel (VP-261) — Stripe → sprzedaże/zwroty w dashboardzie.
 *
 * Webhook-only, per klient: pokazujemy osobisty URL z tokenem (powstaje przy pierwszym
 * zapisie sekretu), user wkleja signing secret (whsec_...) ze Stripe Dashboard.
 * Backend weryfikuje podpis każdego eventu — bez sekretu nic nie wpada.
 * Zastępuje stary wspólny adres /api/webhooks/stripe (bez podpisu) — stąd baner migracyjny.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AlertTriangle, Check, Copy, Loader2, Trash2 } from "lucide-react";
import { STRIPE_SIGNING_SECRET_RE } from "@shared/stripe-tracking";

type StripeTrackingSetup = {
  configured: boolean;
  webhookUrl: string | null;
  lastEventAt: string | null;
  lastEventType: string | null;
  supportedEvents: string[];
};

type Props = { forUserId?: string | null };

const SETUP_URL = "/api/integrations/stripe-tracking/setup";

/** apiRequest rzuca `"<status>: <raw body>"` — użytkownikowi pokazujemy tekst z i18n, nie surowy JSON. */
const isForbidden = (err: Error): boolean => err.message.startsWith("403");

export default function StripeTrackingSetupPanel({ forUserId }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [secret, setSecret] = useState("");
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  const queryKey = forUserId ? ["hub-stripe-tracking", forUserId] : ["hub-stripe-tracking"];
  const withForUser = (url: string) => (forUserId ? `${url}?forUserId=${forUserId}` : url);

  const { data, isLoading } = useQuery<StripeTrackingSetup>({
    queryKey,
    queryFn: () => apiRequest("GET", withForUser(SETUP_URL)),
    staleTime: 30_000,
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["hub-stripe-tracking"] });

  const saveMut = useMutation({
    mutationFn: () =>
      apiRequest("POST", SETUP_URL, {
        signingSecret: secret.trim(),
        ...(forUserId ? { forUserId } : {}),
      }),
    onSuccess: () => {
      setSecret("");
      setFeedback({ type: "ok", msg: t("stripeTracking.feedback.saved") });
      invalidate();
    },
    onError: (err: Error) => {
      setFeedback({
        type: "err",
        msg: t(
          isForbidden(err)
            ? "stripeTracking.feedback.forbidden"
            : "stripeTracking.feedback.saveError"
        ),
      });
    },
  });

  const disconnectMut = useMutation({
    mutationFn: () => apiRequest("DELETE", withForUser(SETUP_URL)),
    onSuccess: () => {
      setFeedback({ type: "ok", msg: t("stripeTracking.feedback.disconnected") });
      invalidate();
    },
    onError: (err: Error) => {
      setFeedback({
        type: "err",
        msg: t(
          isForbidden(err)
            ? "stripeTracking.feedback.forbidden"
            : "stripeTracking.feedback.disconnectError"
        ),
      });
    },
  });

  const handleCopy = async () => {
    if (!data?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(data.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  if (isLoading) {
    return <Loader2 className="w-4 h-4 animate-spin text-white/30" />;
  }

  const configured = data?.configured ?? false;
  const secretLooksValid = STRIPE_SIGNING_SECRET_RE.test(secret.trim());

  return (
    <div className="space-y-3">
      {/* Baner migracyjny — stary wspólny adres bez podpisu został wyłączony */}
      <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-500/25 bg-amber-500/10 text-xs text-amber-200/90 leading-relaxed">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
        <span>{t("stripeTracking.migrationNotice")}</span>
      </div>

      {/* Krok 1 — sekret podpisu */}
      <div className="space-y-1.5">
        <p className="text-xs text-white/50">{t("stripeTracking.step1")}</p>
        <div className="flex items-center gap-2">
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="whsec_..."
            className="flex-1 px-3 py-2 text-xs rounded-md bg-white/[0.04] border border-white/[0.1] text-white/80 font-mono placeholder:text-white/25"
          />
          <button
            onClick={() => saveMut.mutate()}
            disabled={!secretLooksValid || saveMut.isPending}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white transition-colors"
          >
            {saveMut.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            {configured ? t("stripeTracking.rotate") : t("stripeTracking.save")}
          </button>
        </div>
        <p className="text-[11px] text-white/35">{t("stripeTracking.step1Hint")}</p>
      </div>

      {/* Krok 2 — osobisty URL (dostępny po zapisaniu sekretu) */}
      <div className="space-y-1.5">
        <p className="text-xs text-white/50">{t("stripeTracking.step2")}</p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={data?.webhookUrl ?? ""}
            placeholder={t("stripeTracking.urlPlaceholder")}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 px-3 py-2 text-xs rounded-md bg-white/[0.04] border border-white/[0.1] text-white/80 font-mono placeholder:text-white/25"
          />
          <button
            onClick={handleCopy}
            disabled={!data?.webhookUrl}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-white/[0.1] hover:bg-white/[0.06] disabled:opacity-50 text-white/70 transition-colors"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-emerald-400" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            {copied ? t("stripeTracking.copied") : t("stripeTracking.copy")}
          </button>
        </div>
      </div>

      {/* Krok 3 — eventy do zaznaczenia w Stripe */}
      <div className="space-y-1.5">
        <p className="text-xs text-white/50">{t("stripeTracking.step3")}</p>
        <div className="flex gap-1.5 flex-wrap">
          {(data?.supportedEvents ?? []).map((ev) => (
            <span
              key={ev}
              className="text-[11px] font-mono px-2 py-0.5 rounded border border-white/[0.1] bg-white/[0.03] text-white/70"
            >
              {ev}
            </span>
          ))}
        </div>
      </div>

      {/* Status + rozłącz */}
      {configured && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-white/50">
            {data?.lastEventAt
              ? t("stripeTracking.lastEvent", {
                  type: data.lastEventType ?? "—",
                  date: new Date(data.lastEventAt).toLocaleString(i18n.language),
                })
              : t("stripeTracking.noEventsYet")}
          </span>
          <button
            onClick={() => disconnectMut.mutate()}
            disabled={disconnectMut.isPending}
            className="inline-flex items-center gap-1.5 text-red-400/80 hover:text-red-400 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t("stripeTracking.disconnect")}
          </button>
        </div>
      )}

      {feedback && (
        <p className={`text-xs ${feedback.type === "ok" ? "text-emerald-400" : "text-red-400"}`}>
          {feedback.msg}
        </p>
      )}
    </div>
  );
}
